import { Grid } from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import React from 'react';

import type { ResourceError } from 'lib/api/resources';
import useApiQuery from 'lib/api/useApiQuery';
import useFetch from 'lib/hooks/useFetch';
import { STATS_COUNTER } from 'stubs/stats';
import StatsWidget from 'ui/shared/stats/StatsWidget';

import DataFetchAlert from '../shared/DataFetchAlert';

const UNITS_WITHOUT_SPACE = [ 's' ];

const MOVA_SUPPLY_API_URL = 'https://zilwwyrgetjplvwcowjl.supabase.co/functions/v1/mova-supply';

type MovaSupplyResponse = {
  supply: string;
  csupply: string;
};

const MOVA_SUPPLY_PLACEHOLDER: MovaSupplyResponse = {
  supply: '100000000',
  csupply: '20000000',
};

const formatCounterValue = (value: string, units?: string) => {
  let unitsStr = '';
  if (units && UNITS_WITHOUT_SPACE.includes(units)) {
    unitsStr = units;
  } else if (units) {
    unitsStr = units === 'ETH' ? ' MOVA' : ' ' + units;
  }

  return `${ Number(value).toLocaleString(undefined, { maximumFractionDigits: 3, notation: 'compact' }) }${ unitsStr }`;
};

const NumberWidgetsList = () => {
  const fetch = useFetch();

  const { data, isPlaceholderData, isError } = useApiQuery('stats:counters', {
    queryOptions: {
      placeholderData: { counters: Array(10).fill(STATS_COUNTER) },
    },
  });

  const supplyQuery = useQuery<unknown, ResourceError<unknown>, MovaSupplyResponse>({
    queryKey: [ 'mova-supply' ],
    queryFn: async() => fetch(MOVA_SUPPLY_API_URL, undefined, { resource: 'mova-supply' }),
    placeholderData: MOVA_SUPPLY_PLACEHOLDER,
    staleTime: 5 * 60 * 1000,
  });

  if (isError) {
    return <DataFetchAlert/>;
  }

  return (
    <Grid
      gridTemplateColumns={{ base: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' }}
      gridGap={{ base: 1, lg: 2 }}
    >
      {
        data?.counters?.map(({ id, title, value, units, description }, index) => {
          title = title.replace(/ETH/g, 'MOVA');

          return (
            <StatsWidget
              key={ id + (isPlaceholderData ? index : '') }
              label={ title }
              value={ formatCounterValue(value, units) }
              isLoading={ isPlaceholderData }
              hint={ description }
            />
          );
        })
      }
      { !supplyQuery.isError && supplyQuery.data && (
        <>
          <StatsWidget
            key="total-supply"
            label="Total supply"
            value={ formatCounterValue(supplyQuery.data.supply) + ' MOVA' }
            isLoading={ supplyQuery.isPlaceholderData }
            hint="Total MOVA supply"
          />
          <StatsWidget
            key="circulating-supply"
            label="Circulating supply"
            value={ formatCounterValue(supplyQuery.data.csupply) + ' MOVA' }
            isLoading={ supplyQuery.isPlaceholderData }
            hint="Circulating MOVA supply"
          />
        </>
      ) }
    </Grid>
  );
};

export default NumberWidgetsList;
